import { useEffect, useState } from 'react'
import { api, type SeatHealth, type SystemStatus } from '../lib/api.ts'
import { Button } from '../components/Button.tsx'
import { icons } from '../components/icons.ts'

/**
 * What is up, and how quickly it answered.
 *
 * ## Why the durations are as prominent as the ticks
 *
 * A dependency that answers in four seconds is not healthy, and a green light
 * cannot say so. Firebird over a VPN degrades long before it fails — V1
 * measured its pagination running ~2.5× slower under concurrent load — and
 * "slow" is the shape almost every real incident here takes on its way to
 * "down".
 *
 * ## Why the server does not get a tick
 *
 * A process cannot meaningfully report that it is alive: if it were not,
 * nothing would answer, so the answer is produced by the very condition it
 * claims to test. What it *can* report is what an outside observer cannot see
 * — which build answered, how long it has been up, how much memory it holds.
 *
 * Uptime is the one worth reading. A restart loop looks perfectly healthy from
 * outside, because each new process answers immediately; a number that keeps
 * resetting to seconds is how you find it.
 */

const LABEL: Readonly<Record<string, string>> = {
  postgres: 'Nosso banco',
  erp: 'Banco da farmácia (ERP)',
  temporal: 'Temporal',
}

/** Above this, a dependency is answering but not comfortably. */
const SLOW_MS = 1000

/** The steps, named as somebody who does not read the registry would. */
const SEAT_LABEL: Readonly<Record<string, string>> = {
  reader: 'Leitor da receita',
  resolver: 'Resolução de insumos',
  explainer: 'Explicação das divergências',
  classifier: 'Triagem de anexos',
  orienter: 'Orientação da página',
  volume: 'Cálculo de volume',
  adversary: 'Revisão adversarial',
  strength: 'Dosagem no nome do produto',
}

const SEAT_STATE: Readonly<Record<SeatHealth['state'], { label: string; className: string }>> = {
  up: { label: 'No ar', className: 'text-text-brand' },
  /*
   * Amber, like a slow dependency, and for the same reason: this is the state
   * that looks like success from everywhere else. The step is finishing
   * verifications on a model the golden set never measured, and nothing but
   * this row would ever say so.
   */
  partial: { label: 'Parcial — no fallback', className: 'text-text-warning' },
  down: { label: 'Fora do ar', className: 'text-text-error' },
}

/** A model and its uptime, as one cell reads it. */
function modelLine(model: string, uptime: number | undefined): string {
  const short = model.startsWith('openrouter/') ? model.slice('openrouter/'.length) : model
  return uptime === undefined ? short : `${short} · ${uptime.toFixed(1)}%`
}

export function Status({ onBack }: { onBack: () => void }) {
  const [status, setStatus] = useState<SystemStatus | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [checking, setChecking] = useState(false)

  function load() {
    setChecking(true)
    void api
      .status()
      .then((r) => {
        setStatus(r)
        setError(undefined)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao verificar'),
      )
      .finally(() => setChecking(false))
  }

  useEffect(load, [])

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Status</h1>
        <span className="flex-1" />
        <Button variant="secondary" tone="neutral" size="small" disabled={checking} onClick={load}>
          {checking ? 'Verificando…' : 'Verificar de novo'}
        </Button>
      </div>

      <p className="text-tP text-text-weak max-w-prose">
        Cada dependência é consultada agora, não lida de um cache. O tempo de resposta importa tanto
        quanto o sinal verde: um banco que responde em quatro segundos não está bem, e quase todo
        incidente aqui passa por “lento” antes de chegar em “fora”.
      </p>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

      {status !== undefined && (
        <>
          <section className="flex flex-col gap-2">
            <h2 className="text-tPSemi text-text-strong">Dependências</h2>
            <div className="rounded-base border-stroke-weak bg-background-raised w-full overflow-x-auto border">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {['Serviço', 'Situação', 'Resposta', 'Detalhe'].map((h) => (
                      <th
                        key={h}
                        scope="col"
                        className="border-stroke-weak bg-background-sunken text-tPSemi text-text-strong border-b px-3 py-3 text-left"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {status.dependencies.map((dependency) => (
                    <tr key={dependency.name}>
                      <td className="border-stroke-weak text-tP text-text-strong border-b px-3 py-3">
                        {LABEL[dependency.name] ?? dependency.name}
                      </td>
                      <td className="border-stroke-weak text-tPSemi border-b px-3 py-3">
                        <span className={dependency.ok ? 'text-text-brand' : 'text-text-error'}>
                          {dependency.ok ? 'Respondendo' : 'Sem resposta'}
                        </span>
                      </td>
                      {/* Amber for slow-but-alive: the status a green tick hides. */}
                      <td
                        className={`border-stroke-weak text-tP border-b px-3 py-3 tabular-nums ${
                          !dependency.ok
                            ? 'text-text-weak'
                            : dependency.ms > SLOW_MS
                              ? 'text-text-warning'
                              : 'text-text-weak'
                        }`}
                      >
                        {dependency.ms} ms
                        {dependency.ok && dependency.ms > SLOW_MS && ' · lento'}
                      </td>
                      <td className="border-stroke-weak text-tSmall text-text-weak border-b px-3 py-3">
                        {dependency.error ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <h2 className="text-tPSemi text-text-strong">Modelos por etapa</h2>
            <p className="text-tSmall text-text-weak max-w-prose">
              Consultado na OpenRouter, sem gastar nada: a pergunta é quais provedores estão
              servindo cada modelo, não uma chamada de teste. “Parcial” é o estado que importa — a
              etapa continua entregando, no modelo de fallback, que não é o que o golden set mediu.
            </p>
            {status.seats === null ? (
              <p className="text-tSmall text-text-weak">
                Sem resposta da OpenRouter. Isso não quer dizer que os modelos estão no ar — quer
                dizer que não deu para perguntar.
              </p>
            ) : (
              <div className="rounded-base border-stroke-weak bg-background-raised w-full overflow-x-auto border">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      {['Etapa', 'Situação', 'Modelo', 'Fallback'].map((h) => (
                        <th
                          key={h}
                          scope="col"
                          className="border-stroke-weak bg-background-sunken text-tPSemi text-text-strong border-b px-3 py-3 text-left"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {status.seats.map((seat) => (
                      <tr key={seat.agent}>
                        <td className="border-stroke-weak text-tP text-text-strong border-b px-3 py-3">
                          {SEAT_LABEL[seat.agent] ?? seat.agent}
                        </td>
                        <td
                          className={`border-stroke-weak text-tPSemi border-b px-3 py-3 ${SEAT_STATE[seat.state].className}`}
                        >
                          {SEAT_STATE[seat.state].label}
                        </td>
                        <td className="border-stroke-weak text-tSmall border-b px-3 py-3">
                          <span className={seat.primary.ok ? 'text-text-weak' : 'text-text-error'}>
                            {modelLine(seat.primary.model, seat.primary.uptime)}
                          </span>
                        </td>
                        <td className="border-stroke-weak text-tSmall text-text-weak border-b px-3 py-3">
                          {seat.fallbacks.length === 0
                            ? /*
                               * Worth saying out loud rather than leaving blank.
                               * A step with no chain is one whose failure is an
                               * outage, and that is a decision somebody should
                               * be able to see they made.
                               */
                              'sem fallback'
                            : seat.fallbacks.map((f) => (
                                <span
                                  key={f.model}
                                  className={`block ${f.ok ? '' : 'text-text-error'}`}
                                >
                                  {modelLine(f.model, f.uptime)}
                                </span>
                              ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <h2 className="text-tPSemi text-text-strong">Este servidor</h2>
            <p className="text-tSmall text-text-weak max-w-prose">
              Sem sinal verde, de propósito: um processo não consegue atestar que está no ar — se
              não estivesse, nada responderia. O que ele sabe e ninguém de fora sabe é qual build
              respondeu e há quanto tempo. Um número que volta para poucos segundos toda hora é como
              se descobre um processo reiniciando em loop.
            </p>
            <div className="flex flex-wrap gap-4">
              <Fact label="Versão" value={status.instance.version} />
              <Fact label="No ar há" value={uptime(status.instance.uptimeSeconds)} />
              <Fact label="Memória" value={`${status.instance.memoryMb} MB`} />
              <Fact label="Processo" value={String(status.instance.pid)} />
            </div>
          </section>
        </>
      )}
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-0.5 border px-4 py-2">
      <span className="text-tTiny text-text-weak font-semibold uppercase">{label}</span>
      <span className="text-tP text-text-strong tabular-nums">{value}</span>
    </div>
  )
}

function uptime(seconds: number): string {
  if (seconds < 90) return `${seconds}s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 90) return `${minutes} min`
  const hours = Math.round(minutes / 60)
  return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} d`
}
